import api from './api';
import type { WerkplekSignal } from './professionalWerkplekApi';

/**
 * Centraal professional-dashboard (Onderdeel 55). Server:
 * src/routes/professionalWerkplek.ts (vensters, to-do's, schakelbord) en
 * src/routes/agenda.ts (afspraakverzoeken goedkeuren / tegenvoorstel).
 */

const BASIS = '/v1/professional/dashboard';

export type DashboardVensterId = 'vandaag' | 'boekingen' | 'inkoop' | 'administratie';

export interface AgendaItem {
  id: string;
  soort: 'medewerker' | 'intervisie' | 'b2b' | 'kennismaking' | 'blokkade' | 'todo';
  start: string | null;
  eind: string | null;
  titel: string | null;
  casus: string | null;
  organisatie: string | null;
  locatie_type: string | null;
  locatie: string | null;
  video_url: string | null;
  deelnemen_vanaf: string | null;
  prioriteit: boolean;
  link: string;
}

export type DashboardVenster =
  | { id: 'vandaag'; badge_counter: number; items: AgendaItem[] }
  | { id: Exclude<DashboardVensterId, 'vandaag'>; badge_counter: number; items: WerkplekSignal[] };

export interface PersoonlijkeTodo {
  id: string;
  tekst: string;
  gepland_op: string | null;
  voltooid: boolean;
  voltooid_op: string | null;
  created_at: string;
}

export interface TodoArchief {
  open: PersoonlijkeTodo[];
  voltooid: PersoonlijkeTodo[];
  /** Vertrouwenspersoon: de invoer waarschuwt voor namen en casusdetails. */
  tekstgrendel: boolean;
}

export const KANAAL_CATEGORIEEN = ['pool_uitnodiging', 'contract', 'afspraak_aanvraag', 'job_radar', 'urensluiting', 'todo'] as const;
export type KanaalCategorie = (typeof KANAAL_CATEGORIEEN)[number];
export type KanaalKeuze = 'email' | 'sms' | 'beide' | 'geen';

export interface RadarInstellingen {
  actief: boolean;
  disciplines: string[];
  volgt_profiel: boolean;
  radius_km: 15 | 30 | 50 | null;
  profiel_radius_km: number | null;
  opgeslagen: boolean;
}

export interface Schakelbord {
  radar: RadarInstellingen;
  eigen_disciplines: string[];
  radius_opties: number[];
  locatie_bekend: boolean;
  kanalen: Record<KanaalCategorie, KanaalKeuze>;
}

export interface Alternatieven {
  duur_minuten: number;
  huidige_start: string;
  huidige_locatie_type: LocatieType;
  starts: Array<{ start_tijd: string; eind_tijd: string }>;
}

export type LocatieType = 'ONLINE' | 'WERKGEVER' | 'EXTERN';

/** Eigen blokkade van een opdrachtgever (migratie 188); blokkades van de andere kant zijn niet zichtbaar. */
export interface GeblokkeerdeOpdrachtgever {
  wederpartij_id: string;
  naam: string;
  reden: string | null;
  sinds: string;
}

export const DASHBOARD_KEY = ['professional-dashboard-vensters'];
export const BLOKKADES_KEY = ['professional-dashboard-blokkades'];
export const TODO_KEY = ['professional-dashboard-todos'];
export const SCHAKELBORD_KEY = ['professional-dashboard-schakelbord'];

export const dashboardApi = {
  vensters: async (): Promise<DashboardVenster[]> => (await api.get(`${BASIS}/signals`)).data.data.vensters,

  todos: async (): Promise<TodoArchief> => (await api.get(`${BASIS}/todos`)).data.data,
  maakTodo: async (invoer: { tekst: string; gepland_op: string | null }): Promise<PersoonlijkeTodo> =>
    (await api.post(`${BASIS}/todos`, invoer)).data.data,
  wijzigTodo: async (id: string, wijziging: { tekst?: string; gepland_op?: string | null; voltooid?: boolean }): Promise<PersoonlijkeTodo> =>
    (await api.patch(`${BASIS}/todos/${id}`, wijziging)).data.data,
  verwijderTodo: async (id: string): Promise<void> => {
    await api.delete(`${BASIS}/todos/${id}`);
  },

  schakelbord: async (): Promise<Schakelbord> => (await api.get(`${BASIS}/schakelbord`)).data.data,
  zetSchakelbord: async (invoer: {
    radar: Pick<RadarInstellingen, 'actief' | 'disciplines' | 'volgt_profiel' | 'radius_km'>;
    kanalen: Record<KanaalCategorie, KanaalKeuze>;
  }): Promise<Schakelbord> => (await api.put(`${BASIS}/schakelbord`, invoer)).data.data,

  blokkades: async (): Promise<GeblokkeerdeOpdrachtgever[]> => (await api.get(`${BASIS}/blokkades`)).data.data,
  blokkeer: async (employerId: string): Promise<GeblokkeerdeOpdrachtgever[]> =>
    (await api.post(`${BASIS}/blokkades`, { employer_id: employerId })).data.data,
  deblokkeer: async (employerId: string): Promise<void> => {
    await api.delete(`${BASIS}/blokkades/${employerId}`);
  },

  keurGoed: async (afspraakId: string): Promise<void> => {
    await api.post(`/agenda/professional/afspraken/${afspraakId}/goedkeuren`);
  },
  alternatieven: async (afspraakId: string): Promise<Alternatieven> =>
    (await api.get(`/agenda/professional/afspraken/${afspraakId}/alternatieven`)).data.data,
  tegenvoorstel: async (
    afspraakId: string,
    voorstel: { start_tijd: string; locatie_type: LocatieType; locatie_omschrijving?: string | null },
  ): Promise<void> => {
    await api.post(`/agenda/professional/afspraken/${afspraakId}/tegenvoorstel`, voorstel);
  },
};
